/**
 * Discriminated union of all sign-in failure modes the UI cares about.
 *
 * Cognito's underlying errors are mapped to one of these — keeping the UI
 * decoupled from `amazon-cognito-identity-js` error names lets us swap the
 * library out later (or wrap with retries, telemetry, etc.) without
 * touching the UI's error-handling code.
 */
export type SignInError =
  /**
   * Wrong email or wrong password — Cognito doesn't distinguish when
   * `prevent_user_existence_errors = ENABLED` (which staging/production do).
   * UI shows "Incorrect email or password" regardless.
   */
  | { readonly kind: 'invalid-credentials' }
  /**
   * User signed up but never entered their verification code.
   * Carries email so the UI can hop to /confirm with it prefilled.
   */
  | { readonly kind: 'user-not-confirmed'; readonly email: string }
  /**
   * An admin reset the user's password. They must run the forgot-password
   * flow to set a new one before they can sign in.
   */
  | { readonly kind: 'password-reset-required'; readonly email: string }
  /** Rate-limited by Cognito. */
  | { readonly kind: 'too-many-requests' }
  /** Anything we didn't anticipate. UI shows the message verbatim. */
  | { readonly kind: 'unknown'; readonly message: string };

interface CognitoErrorShape {
  name?: string;
  message?: string;
}

function asCognitoError(value: unknown): CognitoErrorShape | null {
  if (typeof value !== 'object' || value === null) return null;
  // CognitoErrorShape has only optional properties, so `object` is
  // structurally assignable to it without a cast.
  return value;
}

export function mapSignInError(rawError: unknown, email: string): SignInError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return { kind: 'invalid-credentials' };
    case 'UserNotConfirmedException':
      return { kind: 'user-not-confirmed', email };
    case 'PasswordResetRequiredException':
      return { kind: 'password-reset-required', email };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? 'Sign in failed. Please try again.',
      };
  }
}

/**
 * Sign-up failure modes the UI cares about.
 */
export type SignUpError =
  /** Cognito already has a user with this email. Surfaces as "already in use". */
  | { readonly kind: 'email-already-in-use' }
  /**
   * Cognito's `InvalidPasswordException` — the password didn't satisfy the
   * pool's policy. Our client-side strength meter should catch this first;
   * if it slips through, surface verbatim so the user knows what to fix.
   */
  | { readonly kind: 'invalid-password'; readonly message: string }
  /** Email format failed Cognito's own check (after passing Zod's). */
  | { readonly kind: 'invalid-email' }
  /** Rate-limited. */
  | { readonly kind: 'too-many-requests' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapSignUpError(rawError: unknown): SignUpError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'UsernameExistsException':
      return { kind: 'email-already-in-use' };
    case 'InvalidPasswordException':
      return {
        kind: 'invalid-password',
        message: err?.message ?? 'Password did not meet the requirements.',
      };
    case 'InvalidParameterException':
      // Cognito uses this for malformed email among other things. The
      // message tells us which — for MVP we surface generically; revisit
      // when we have telemetry on what users actually see.
      return { kind: 'invalid-email' };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? 'Sign up failed. Please try again.',
      };
  }
}

/**
 * Confirm-sign-up failure modes (entering the email verification code).
 */
export type ConfirmError =
  | { readonly kind: 'code-mismatch' }
  | { readonly kind: 'code-expired' }
  /** User typed a non-numeric or wrong-length code — Cognito rejects pre-validation. */
  | { readonly kind: 'invalid-code-format' }
  | { readonly kind: 'too-many-requests' }
  /** Cognito has no record of this email. Edge case; usually means the wizard lost state. */
  | { readonly kind: 'user-not-found' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapConfirmError(rawError: unknown): ConfirmError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'CodeMismatchException':
      return { kind: 'code-mismatch' };
    case 'ExpiredCodeException':
      return { kind: 'code-expired' };
    case 'InvalidParameterException':
      return { kind: 'invalid-code-format' };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'UserNotFoundException':
      return { kind: 'user-not-found' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't confirm the code. Please try again.",
      };
  }
}

/**
 * Resend-confirmation-code failure modes.
 */
export type ResendError =
  | { readonly kind: 'too-many-requests' }
  | { readonly kind: 'user-not-found' }
  | { readonly kind: 'already-confirmed' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapResendError(rawError: unknown): ResendError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'UserNotFoundException':
      return { kind: 'user-not-found' };
    case 'InvalidParameterException':
      // Cognito raises InvalidParameterException when ResendConfirmationCode
      // is called for an already-confirmed user.
      return { kind: 'already-confirmed' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't resend the code. Please try again.",
      };
  }
}

/**
 * `ForgotPassword` (sending the reset code) failure modes.
 *
 * With `prevent_user_existence_errors = ENABLED` on the app client,
 * Cognito does NOT distinguish "user doesn't exist" from success — both
 * paths return a generic success response. So we don't need a
 * user-not-found variant here.
 */
export type ForgotPasswordError =
  | { readonly kind: 'too-many-requests' }
  /** Returned when an account is in a state that can't initiate a reset (e.g. unconfirmed). */
  | { readonly kind: 'not-authorized'; readonly message: string }
  | { readonly kind: 'invalid-email' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapForgotPasswordError(rawError: unknown): ForgotPasswordError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'NotAuthorizedException':
      return {
        kind: 'not-authorized',
        message: err?.message ?? "We can't reset that account right now.",
      };
    case 'InvalidParameterException':
      return { kind: 'invalid-email' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't start the password reset. Please try again.",
      };
  }
}

/**
 * `ConfirmForgotPassword` (entering the code + new password) failure modes.
 */
export type ResetPasswordError =
  | { readonly kind: 'code-mismatch' }
  | { readonly kind: 'code-expired' }
  | { readonly kind: 'invalid-code-format' }
  /** New password didn't satisfy the Cognito password policy. */
  | { readonly kind: 'invalid-password'; readonly message: string }
  | { readonly kind: 'too-many-requests' }
  | { readonly kind: 'user-not-found' }
  | { readonly kind: 'unknown'; readonly message: string };

/**
 * `RespondToAuthChallenge` (SOFTWARE_TOKEN_MFA — submitting the 6-digit
 * code from the user's authenticator app during sign-in) failure modes.
 */
export type MfaChallengeError =
  | { readonly kind: 'code-mismatch' }
  | { readonly kind: 'code-expired' }
  | { readonly kind: 'invalid-code-format' }
  | { readonly kind: 'too-many-requests' }
  /** Session token expired — Cognito challenge sessions only live for ~3 min. */
  | { readonly kind: 'session-expired' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapMfaChallengeError(rawError: unknown): MfaChallengeError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'CodeMismatchException':
      return { kind: 'code-mismatch' };
    case 'ExpiredCodeException':
      return { kind: 'code-expired' };
    case 'InvalidParameterException':
      return { kind: 'invalid-code-format' };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'NotAuthorizedException':
      // Cognito returns NotAuthorizedException when the challenge session
      // has expired (3-minute TTL). The user has to re-enter their password
      // to get a fresh challenge.
      return { kind: 'session-expired' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't verify the code. Please try again.",
      };
  }
}

/**
 * `AssociateSoftwareToken` + `VerifySoftwareToken` + `SetUserMFAPreference`
 * — the three calls that make up TOTP enrolment. They share a small set of
 * failure modes.
 */
export type MfaSetupError =
  | { readonly kind: 'code-mismatch' }
  | { readonly kind: 'invalid-code-format' }
  | { readonly kind: 'too-many-requests' }
  /** Access token expired — user has to sign in again before enrolling. */
  | { readonly kind: 'session-expired' }
  | { readonly kind: 'unknown'; readonly message: string };

export function mapMfaSetupError(rawError: unknown): MfaSetupError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'EnableSoftwareTokenMFAException':
      // Cognito's own "wrong code" exception for VerifySoftwareToken.
      return { kind: 'code-mismatch' };
    case 'CodeMismatchException':
      return { kind: 'code-mismatch' };
    case 'InvalidParameterException':
      return { kind: 'invalid-code-format' };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'NotAuthorizedException':
      return { kind: 'session-expired' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't set up MFA. Please try again.",
      };
  }
}

export function mapResetPasswordError(rawError: unknown): ResetPasswordError {
  const err = asCognitoError(rawError);
  const name = err?.name ?? '';

  switch (name) {
    case 'CodeMismatchException':
      return { kind: 'code-mismatch' };
    case 'ExpiredCodeException':
      return { kind: 'code-expired' };
    case 'InvalidParameterException':
      // For ConfirmForgotPassword, InvalidParameterException usually means
      // a malformed code (non-numeric, wrong length).
      return { kind: 'invalid-code-format' };
    case 'InvalidPasswordException':
      return {
        kind: 'invalid-password',
        message: err?.message ?? 'Password did not meet the requirements.',
      };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return { kind: 'too-many-requests' };
    case 'UserNotFoundException':
      return { kind: 'user-not-found' };
    default:
      return {
        kind: 'unknown',
        message: err?.message ?? "Couldn't reset your password. Please try again.",
      };
  }
}
